import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { BROKER_INTEGRATION_OPTIONS } from '../../constants/brokerIntegrationOptions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formShellStyle = {
  maxWidth: '500px',
  margin: '0 auto',
  padding: '20px',
  background: '#0C1018',
  borderRadius: '10px',
  border: '1px solid #2A3444',
};

const fieldStyle = {
  width: '100%',
  padding: '10px 12px',
  fontSize: '0.9em',
  borderRadius: '4px',
  border: '1px solid #3A4658',
  background: '#141A24',
  color: '#E8EDF4',
  outline: 'none',
  marginBottom: '12px',
  transition: 'border-color 0.2s',
};

/**
 * Supported-brokers waitlist: "Get notified" expands into the same broker + email pattern
 * as the Features page feedback section (broker integration only — no feature-request path).
 */
export default function LandingBrokerWaitlistForm({
  waitlistNote,
  waitlistCopy,
  buttonLabel = 'Get notified',
  landingPageId,
}) {
  const { user } = useContext(AuthContext);
  const [expanded, setExpanded] = useState(false);
  const [broker, setBroker] = useState('');
  const [otherBrokerName, setOtherBrokerName] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user?.email) setEmail(user.email);
  }, [user?.email]);

  const isOther = broker === 'Other';
  const resolvedBroker = isOther ? otherBrokerName.trim() : broker.trim();

  const handleBrokerChange = (value) => {
    setBroker(value);
    if (value !== 'Other') setOtherBrokerName('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!resolvedBroker) {
      setStatus('error');
      return;
    }

    const emailProvided = email && email.trim() !== '';
    if (!user && !emailProvided) {
      setStatus('error');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!user && emailProvided && !emailRegex.test(email.trim())) {
      setStatus('error');
      return;
    }

    setSubmitting(true);
    setStatus(null);

    const emailToUse = emailProvided ? email.trim() : user?.email || 'feedback@precisiontrader.com';
    const subject = `Broker Integration Request: ${resolvedBroker}`;
    let message = `Broker Request: ${resolvedBroker}`;
    if (isOther) {
      message += '\n(Selected: Other)';
    }
    if (landingPageId) {
      message += `\n\nLanding variant: ${landingPageId}`;
    }
    message += `\nPage URL: ${window.location.href}`;
    if (!emailProvided && !user?.email) {
      message += '\n\n(No email provided - user requested to remain anonymous)';
    }

    if (!BACKEND_URL) {
      setStatus('error');
      setSubmitting(false);
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user?.token || ''}`,
        },
        body: JSON.stringify({
          email: emailToUse,
          subject,
          message,
          userId: user?.id || null,
          timestamp: new Date().toISOString(),
        }),
      });

      if (response.ok) {
        setStatus('success');
        setBroker('');
        setOtherBrokerName('');
        setEmail(user?.email || '');
        setTimeout(() => setStatus(null), 5000);
      } else {
        setStatus('error');
      }
    } catch (err) {
      console.error('Broker waitlist submit error:', err);
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const emailOk =
    !!user || (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()));
  const canSubmit = Boolean(resolvedBroker) && emailOk;

  if (status === 'success') {
    return (
      <div className="landing-brokers-waitlist-below">
        <div style={formShellStyle}>
          <div
            style={{
              padding: '8px',
              borderRadius: '4px',
              background: 'rgba(7, 203, 163, 0.14)',
              border: '1px solid rgba(7, 203, 163, 0.4)',
              color: '#07CBA3',
              fontSize: '0.85em',
            }}
          >
            Thank you!
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="landing-brokers-waitlist-below">
      {waitlistNote && <p className="landing-broker-waitlist-note">{waitlistNote}</p>}
      {waitlistCopy && <p className="landing-broker-waitlist-copy">{waitlistCopy}</p>}

      {!expanded ? (
        <div className="landing-broker-waitlist-collapsed">
          <button type="button" className="landing-cta-btn" onClick={() => setExpanded(true)}>
            {buttonLabel}
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} style={formShellStyle}>
          <select
            name="broker"
            value={broker}
            onChange={(ev) => handleBrokerChange(ev.target.value)}
            className="landing-broker-waitlist-field"
            style={fieldStyle}
            required
          >
            <option value="">Select broker...</option>
            {BROKER_INTEGRATION_OPTIONS.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>

          {isOther && (
            <input
              type="text"
              name="otherBrokerName"
              value={otherBrokerName}
              onChange={(ev) => setOtherBrokerName(ev.target.value)}
              placeholder="Broker name (required)"
              className="landing-broker-waitlist-field"
              style={fieldStyle}
              required
              autoComplete="organization"
            />
          )}

          {!user && (
            <input
              type="email"
              name="email"
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
              placeholder="Email (required)"
              className="landing-broker-waitlist-field"
              style={fieldStyle}
            />
          )}

          {status === 'error' && (
            <div
              style={{
                padding: '8px',
                marginBottom: '12px',
                borderRadius: '4px',
                background: 'rgba(247, 50, 99, 0.14)',
                border: '1px solid rgba(247, 50, 99, 0.4)',
                color: '#F73263',
                fontSize: '0.85em',
              }}
            >
              {isOther && !otherBrokerName.trim()
                ? 'Enter the broker name.'
                : !user && !(email && email.trim())
                  ? 'Enter your email so we can reach you.'
                  : 'Error. Try again.'}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !canSubmit}
            className="landing-broker-waitlist-submit"
          >
            {submitting ? 'Sending...' : 'Send'}
          </button>
        </form>
      )}
    </div>
  );
}
